import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';

export default function ProtectedRoute({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { subscription, loading: subLoading, blocked } = useSubscription();
  const location = useLocation();
  
  // Timeout para evitar loading infinito na verificação de assinatura
  const [timeoutReached, setTimeoutReached] = useState(false);
  
  useEffect(() => {
    if (subLoading) {
      const timeout = setTimeout(() => {
        setTimeoutReached(true);
      }, 6000);
      return () => clearTimeout(timeout);
    } else {
      setTimeoutReached(false);
    }
  }, [subLoading]);
  
  // Exceção para adm@revicont.dev - sempre permitir acesso
  const isMasterAdmin = user?.email === 'adm@revicont.dev' || (user?.role === 'admin' && !user?.companyId);

  // Timeout para authLoading - se passar de 3 segundos, permitir acesso
  const [authTimeout, setAuthTimeout] = useState(false);
  
  useEffect(() => {
    if (authLoading) {
      const timeout = setTimeout(() => {
        setAuthTimeout(true);
      }, 3000);
      return () => clearTimeout(timeout);
    } else {
      setAuthTimeout(false);
    }
  }, [authLoading]);

  if (authLoading && !authTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Exceção para adm@revicont.dev ou admin sem companyId - sempre permitir acesso
  if (isMasterAdmin) {
    return children;
  }

  // Permitir acesso a /billing, /checkout e /subscription-blocked mesmo quando bloqueado ou sem assinatura
  // Verificar ANTES de carregar assinatura para permitir acesso rápido
  const allowedPages = ['/billing', '/checkout', '/subscription-blocked'];
  if (allowedPages.includes(location.pathname)) {
    return children;
  }

  // Aguardar carregamento da assinatura apenas se usuário estiver logado
  // Adicionar timeout para evitar loading infinito
  if (subLoading) {
    // Verificar se há informação de bloqueio no localStorage enquanto carrega
    const isBlockedInStorage = localStorage.getItem('subscriptionBlocked') === 'true';
    if (isBlockedInStorage && !allowedPages.includes(location.pathname)) {
      return <Navigate to="/billing" replace />;
    }
    
    // Se timeout foi atingido, permitir acesso mesmo sem assinatura carregada
    if (timeoutReached) {
      return children;
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Verificando assinatura...</p>
        </div>
      </div>
    );
  }

  // Se está bloqueado, redirecionar para página de bloqueio
  if (blocked) {
    localStorage.setItem('subscriptionBlocked', 'true');
    return <Navigate to="/billing" replace />;
  }

  // Limpar flag de bloqueio se não estiver bloqueado
  if (!blocked) {
    localStorage.removeItem('subscriptionBlocked');
  }

  // Se não tem assinatura E não está na página de billing ou checkout, redirecionar
  if (!subscription && !allowedPages.includes(location.pathname)) {
    return <Navigate to="/billing" replace />;
  }

  return children;
}



